/**
 * Simulation Adapter Layer
 *
 * Maps frontend configs → API payloads, normalizes responses → UI schema.
 * Handles errors, retries (max 2), timeouts, and mode switching (live/mock/hybrid).
 */

import type {
  RunSimulationRequest,
  RunSimulationResponse,
  ReplaySimulationRequest,
  ReplaySimulationResponse,
  StressSimulationRequest,
  StressSimulationResponse,
  SimulationResult,
  SimulationError,
  SimulationErrorCode,
  SimulationErrorType,
  SimulationAdapterConfig,
  SimulationMode,
  GatewayResponse,
  SimulationMetrics,
  SimulationSummary,
  SimulationPosition,
  TimelineEntry,
  PollingOptions,
  PollingController,
  ResultValidationRule,
} from '../types/simulation';

import { ERROR_CODE_TO_TYPE } from '../types/simulation';

import {
  mockRunSimulation,
  mockRunReplay,
  mockRunStressTest,
  mockGetSimulationResults,
} from '../mocks/simulationMock';

import { SimulationLogger } from '../store/simulationStore';

import { apiV1Base } from '../lib/apiConfig';
// ═══════════════════════════════════════════════════════════════════════
//  CONFIG
// ═══════════════════════════════════════════════════════════════════════

function getEnv(key: string, fallback: string): string {
  try {
    return (import.meta as any).env?.[key] ?? fallback;
  } catch {
    return fallback;
  }
}

const DEFAULT_CONFIG: SimulationAdapterConfig = {
  baseUrl: getEnv('VITE_GATEWAY_URL',
    apiV1Base()),
  apiKey: getEnv('VITE_API_KEY', 'adversyn-dev-key-2026'),
  mode: (getEnv('VITE_SIMULATION_MODE', 'mock') as SimulationMode),
  timeoutMs: 30_000,
  maxRetries: 2,
};

let config: SimulationAdapterConfig = { ...DEFAULT_CONFIG };

export function configureSimulationAdapter(overrides: Partial<SimulationAdapterConfig>): void {
  config = { ...config, ...overrides };
}

export function getSimulationMode(): SimulationMode {
  return config.mode;
}

// ═══════════════════════════════════════════════════════════════════════
//  INTERNAL: HTTP CLIENT WITH RETRY + TIMEOUT
// ═══════════════════════════════════════════════════════════════════════

async function gatewayFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0,
): Promise<GatewayResponse<T>> {
  const url = `${config.baseUrl}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);

  const startTime = performance.now();
  const requestId = crypto.randomUUID();

  SimulationLogger.requestStart(endpoint, requestId);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey,
        'x-request-id': requestId,
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);
    const durationMs = Math.round(performance.now() - startTime);

    // Rate limited — retry after backoff
    if (response.status === 429) {
      if (retryCount < config.maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 8000);
        SimulationLogger.retrying(endpoint, retryCount + 1, backoffMs);
        await sleep(backoffMs);
        return gatewayFetch<T>(endpoint, options, retryCount + 1);
      }
      SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, 'RATE_LIMITED');
      throw createSimulationError('RATE_LIMITED', 'Rate limit exceeded. Please wait and try again.');
    }

    // Server error — retry
    if (response.status >= 500 && retryCount < config.maxRetries) {
      const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 8000);
      SimulationLogger.retrying(endpoint, retryCount + 1, backoffMs);
      await sleep(backoffMs);
      return gatewayFetch<T>(endpoint, options, retryCount + 1);
    }

    const body = await response.json() as GatewayResponse<T>;

    // Validate gateway response shape
    if (typeof body.success !== 'boolean') {
      SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, 'INVALID_RESPONSE');
      throw createSimulationError('INVALID_RESPONSE', 'Invalid response format from gateway.');
    }

    if (!body.success) {
      SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, body.error?.code || 'SERVER_ERROR');
      throw createSimulationError(
        'SERVER_ERROR',
        body.error?.message || 'Unknown gateway error.',
        { gatewayError: body.error },
      );
    }

    SimulationLogger.requestEnd(endpoint, requestId, durationMs, true);
    return body;
  } catch (err: unknown) {
    clearTimeout(timeoutId);
    const durationMs = Math.round(performance.now() - startTime);

    // Already a SimulationError — rethrow
    if (isSimulationError(err)) throw err;

    // Abort / timeout
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (retryCount < config.maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 8000);
        SimulationLogger.retrying(endpoint, retryCount + 1, backoffMs);
        await sleep(backoffMs);
        return gatewayFetch<T>(endpoint, options, retryCount + 1);
      }
      SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, 'TIMEOUT');
      throw createSimulationError('TIMEOUT', `Request timed out after ${config.timeoutMs}ms.`);
    }

    // Network error
    if (err instanceof TypeError && err.message.includes('fetch')) {
      if (retryCount < config.maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 8000);
        SimulationLogger.retrying(endpoint, retryCount + 1, backoffMs);
        await sleep(backoffMs);
        return gatewayFetch<T>(endpoint, options, retryCount + 1);
      }
      SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, 'NETWORK_ERROR');
      throw createSimulationError('NETWORK_ERROR', 'Network connection failed. Check your connection.');
    }

    SimulationLogger.requestEnd(endpoint, requestId, durationMs, false, 'UNKNOWN');
    throw createSimulationError('UNKNOWN', 'An unexpected error occurred.');
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  PUBLIC API — runSimulation
// ═══════════════════════════════════════════════════════════════════════

export async function runSimulation(
  request: RunSimulationRequest,
): Promise<SimulationResult> {
  if (config.mode === 'mock') {
    return mockRunSimulation(request);
  }

  const payload = mapRunSimulationPayload(request);

  const response = await gatewayFetch<RunSimulationResponse>(
    '/simulation/run',
    { method: 'POST', body: JSON.stringify(payload) },
  );

  if (config.mode === 'hybrid' && !response.data?.result) {
    SimulationLogger.info('Hybrid mode: falling back to mock for runSimulation');
    return mockRunSimulation(request);
  }

  return normalizeSimulationResult(response.data!, 'live');
}

// ═══════════════════════════════════════════════════════════════════════
//  PUBLIC API — runReplay
// ═══════════════════════════════════════════════════════════════════════

export async function runReplay(
  request: ReplaySimulationRequest,
): Promise<SimulationResult> {
  if (config.mode === 'mock') {
    return mockRunReplay(request);
  }

  const payload = mapReplayPayload(request);

  const response = await gatewayFetch<ReplaySimulationResponse>(
    '/simulation/replay',
    { method: 'POST', body: JSON.stringify(payload) },
  );

  if (config.mode === 'hybrid' && !response.data?.replayResult) {
    SimulationLogger.info('Hybrid mode: falling back to mock for runReplay');
    return mockRunReplay(request);
  }

  return normalizeSimulationResult(response.data!, 'replay');
}

// ═══════════════════════════════════════════════════════════════════════
//  PUBLIC API — runStressTest
// ═══════════════════════════════════════════════════════════════════════

export async function runStressTest(
  request: StressSimulationRequest,
): Promise<SimulationResult> {
  if (config.mode === 'mock') {
    return mockRunStressTest(request);
  }

  const payload = mapStressPayload(request);

  const response = await gatewayFetch<StressSimulationResponse>(
    '/simulation/stress',
    { method: 'POST', body: JSON.stringify(payload) },
  );

  if (config.mode === 'hybrid' && !response.data?.result) {
    SimulationLogger.info('Hybrid mode: falling back to mock for runStressTest');
    return mockRunStressTest(request);
  }

  return normalizeSimulationResult(response.data!, 'stress');
}

// ═══════════════════════════════════════════════════════════════════════
//  PUBLIC API — getSimulationResults
// ═══════════════════════════════════════════════════════════════════════

export async function getSimulationResults(
  simulationId: string,
): Promise<SimulationResult> {
  if (config.mode === 'mock') {
    return mockGetSimulationResults(simulationId);
  }

  const response = await gatewayFetch<SimulationResult>(
    `/simulation/${simulationId}`,
    { method: 'GET' },
  );

  if (config.mode === 'hybrid' && !response.data) {
    SimulationLogger.info('Hybrid mode: falling back to mock for getSimulationResults');
    return mockGetSimulationResults(simulationId);
  }

  return normalizeSimulationResult(response.data!, 'live');
}

// ═══════════════════════════════════════════════════════════════════════
//  PAYLOAD MAPPERS — Frontend Config → API Payload
// ═══════════════════════════════════════════════════════════════════════

function mapRunSimulationPayload(req: RunSimulationRequest): Record<string, unknown> {
  return {
    account_id: req.accountId,
    strategy: req.strategy,
    initial_capital: req.initialCapital,
    duration: req.duration,
    risk_tolerance: req.riskTolerance,
    symbols: req.symbols,
    timeframe: req.timeframe ?? '1D',
    max_position_size: req.maxPositionSize ?? 0.1,
    stop_loss: req.stopLoss,
    take_profit: req.takeProfit,
  };
}

function mapReplayPayload(req: ReplaySimulationRequest): Record<string, unknown> {
  return {
    trade_id: req.tradeId,
    symbol: req.symbol,
    start_date: req.startDate,
    end_date: req.endDate,
    speed: req.speed,
    modified_entry: req.modifiedEntry
      ? {
          price: req.modifiedEntry.price,
          quantity: req.modifiedEntry.quantity,
          stop_loss: req.modifiedEntry.stopLoss,
          take_profit: req.modifiedEntry.takeProfit,
        }
      : undefined,
    include_deliberation: req.includeDeliberation ?? false,
  };
}

function mapStressPayload(req: StressSimulationRequest): Record<string, unknown> {
  return {
    account_id: req.accountId,
    scenario: req.scenario,
    severity: req.severity,
    duration: req.duration,
    custom_params: req.customParams
      ? {
          market_drop: req.customParams.marketDrop,
          volatility_multiplier: req.customParams.volatilityMultiplier,
          correlation_shift: req.customParams.correlationShift,
          liquidity_drain: req.customParams.liquidityDrain,
        }
      : undefined,
    symbols: req.symbols,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  RESULT NORMALIZER — API Response → UI Schema (Phase 5)
// ═══════════════════════════════════════════════════════════════════════

export function normalizeSimulationResult(
  raw: Record<string, any>,
  source: 'live' | 'replay' | 'stress',
): SimulationResult {
  // Extract the inner result object depending on response type
  const result = raw.result || raw.replayResult || raw;

  const simulationId: string =
    result.simulationId || result.simulation_id || raw.simulationId || raw.simulation_id || crypto.randomUUID();

  return {
    simulationId,
    status: normalizeStatus(result.status || raw.status),
    summary: normalizeSummary(result.summary || raw.summary || {}, source),
    timeline: normalizeTimeline(result.timeline || raw.timeline || []),
    positions: normalizePositions(result.positions || raw.positions || []),
    metrics: normalizeMetrics(result.metrics || raw.metrics || {}),
  };
}

function normalizeStatus(raw: string | undefined): SimulationResult['status'] {
  const map: Record<string, SimulationResult['status']> = {
    completed: 'success',
    complete: 'success',
    success: 'success',
    done: 'success',
    running: 'running',
    in_progress: 'running',
    queued: 'loading',
    pending: 'loading',
    failed: 'error',
    error: 'error',
  };
  return map[(raw || '').toLowerCase()] || 'success';
}

function normalizeSummary(raw: Record<string, any>, source: string): SimulationSummary {
  return {
    description: raw.description || raw.desc || `${source} simulation`,
    strategy: raw.strategy || raw.strategy_name || 'Unknown Strategy',
    startingCapital: raw.startingCapital ?? raw.starting_capital ?? raw.initial_capital ?? 0,
    finalCapital: raw.finalCapital ?? raw.final_capital ?? raw.ending_capital ?? 0,
    duration: raw.duration || raw.time_period || 'N/A',
    riskLevel: normalizeRiskLevel(raw.riskLevel || raw.risk_level),
    aiConfidence: raw.aiConfidence ?? raw.ai_confidence ?? raw.confidence ?? 0,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
  };
}

function normalizeRiskLevel(raw: string | undefined): SimulationSummary['riskLevel'] {
  const level = (raw || '').toLowerCase();
  if (level.includes('extreme') || level.includes('very high')) return 'Extreme';
  if (level.includes('high')) return 'High';
  if (level.includes('med')) return 'Medium';
  return 'Low';
}

function normalizeTimeline(raw: any[]): TimelineEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => ({
    timestamp: entry.timestamp || entry.time || entry.date || '',
    equity: entry.equity ?? entry.value ?? entry.portfolio_value ?? 0,
    drawdown: entry.drawdown ?? entry.dd ?? 0,
    dailyReturn: entry.dailyReturn ?? entry.daily_return ?? entry.return ?? 0,
    cumulativeReturn: entry.cumulativeReturn ?? entry.cumulative_return ?? entry.cum_return ?? 0,
    benchmark: entry.benchmark ?? entry.bench ?? undefined,
    event: entry.event ?? entry.note ?? undefined,
  }));
}

function normalizePositions(raw: any[]): SimulationPosition[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((pos) => ({
    symbol: pos.symbol || pos.ticker || 'UNKNOWN',
    side: (pos.side || pos.direction || 'BUY').toUpperCase() as SimulationPosition['side'],
    quantity: pos.quantity ?? pos.qty ?? pos.size ?? 0,
    entryPrice: pos.entryPrice ?? pos.entry_price ?? pos.avg_entry ?? 0,
    currentPrice: pos.currentPrice ?? pos.current_price ?? pos.last_price ?? 0,
    unrealizedPnl: pos.unrealizedPnl ?? pos.unrealized_pnl ?? pos.pnl ?? 0,
    unrealizedPnlPct: pos.unrealizedPnlPct ?? pos.unrealized_pnl_pct ?? 0,
    weight: pos.weight ?? pos.allocation ?? 0,
    openedAt: pos.openedAt ?? pos.opened_at ?? pos.entry_time ?? '',
    closedAt: pos.closedAt ?? pos.closed_at ?? pos.exit_time ?? undefined,
  }));
}

function normalizeMetrics(raw: Record<string, any>): SimulationMetrics {
  return {
    totalReturn: raw.totalReturn ?? raw.total_return ?? raw.pnl ?? 0,
    totalReturnPct: raw.totalReturnPct ?? raw.total_return_pct ?? raw.roi ?? 0,
    annualizedReturn: raw.annualizedReturn ?? raw.annualized_return ?? raw.cagr ?? 0,
    sharpeRatio: raw.sharpeRatio ?? raw.sharpe_ratio ?? raw.sharpe ?? 0,
    sortinoRatio: raw.sortinoRatio ?? raw.sortino_ratio ?? raw.sortino ?? 0,
    maxDrawdown: raw.maxDrawdown ?? raw.max_drawdown ?? raw.mdd ?? 0,
    maxDrawdownPct: raw.maxDrawdownPct ?? raw.max_drawdown_pct ?? raw.mdd_pct ?? 0,
    winRate: raw.winRate ?? raw.win_rate ?? 0,
    profitFactor: raw.profitFactor ?? raw.profit_factor ?? 0,
    avgWin: raw.avgWin ?? raw.avg_win ?? 0,
    avgLoss: raw.avgLoss ?? raw.avg_loss ?? 0,
    totalTrades: raw.totalTrades ?? raw.total_trades ?? raw.trade_count ?? 0,
    winningTrades: raw.winningTrades ?? raw.winning_trades ?? 0,
    losingTrades: raw.losingTrades ?? raw.losing_trades ?? 0,
    avgHoldingPeriod: raw.avgHoldingPeriod ?? raw.avg_holding_period ?? 'N/A',
    calmarRatio: raw.calmarRatio ?? raw.calmar_ratio ?? 0,
    volatility: raw.volatility ?? raw.vol ?? raw.annual_vol ?? 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  ERROR HELPERS
// ═══════════════════════════════════════════════════════════════════════

const ERROR_USER_MESSAGES: Record<SimulationErrorCode, string> = {
  NETWORK_ERROR: 'Unable to connect to the simulation engine. Please check your network.',
  TIMEOUT: 'The simulation took too long to respond. Please try again.',
  RATE_LIMITED: 'Too many requests. Please wait a moment before trying again.',
  INVALID_REQUEST: 'Invalid simulation parameters. Please review your inputs.',
  INVALID_RESPONSE: 'Received an unexpected response. Please try again.',
  SERVER_ERROR: 'The simulation engine encountered an error. Please try again later.',
  SIMULATION_FAILED: 'The simulation failed to complete. Check parameters and retry.',
  UNKNOWN: 'An unexpected error occurred. Please try again.',
};

function createSimulationError(
  code: SimulationErrorCode,
  message: string,
  details?: Record<string, unknown>,
): SimulationError {
  return {
    code,
    message,
    userMessage: ERROR_USER_MESSAGES[code],
    retryable: ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMITED', 'SERVER_ERROR'].includes(code),
    details,
  };
}

function isSimulationError(err: unknown): err is SimulationError {
  return typeof err === 'object' && err !== null && 'code' in err && 'userMessage' in err;
}

export { isSimulationError, createSimulationError };

// ═══════════════════════════════════════════════════════════════════════
//  UTILITIES
// ═══════════════════════════════════════════════════════════════════════

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ═══════════════════════════════════════════════════════════════════════
//  POLLING CONTROLLER (Phase 2.1)
// ═══════════════════════════════════════════════════════════════════════

export function pollSimulation(
  simulationId: string,
  options: PollingOptions = {},
): PollingController {
  const {
    intervalMs = 1500,
    timeoutMs = 60_000,
    onUpdate,
    onComplete,
    onError,
    signal: externalSignal,
  } = options;

  const abortController = new AbortController();
  let active = true;

  // Link external signal if provided
  if (externalSignal) {
    if (externalSignal.aborted) {
      active = false;
      abortController.abort();
    } else {
      externalSignal.addEventListener('abort', () => {
        active = false;
        abortController.abort();
      }, { once: true });
    }
  }

  const promise = new Promise<SimulationResult>(async (resolve, reject) => {
    const startTime = performance.now();

    SimulationLogger.info('Polling started', { simulationId, intervalMs, timeoutMs });

    while (active) {
      // Check timeout
      const elapsed = performance.now() - startTime;
      if (elapsed >= timeoutMs) {
        active = false;
        const err = createSimulationError('TIMEOUT', `Polling timed out after ${timeoutMs}ms.`);
        onError?.(err);
        reject(err);
        return;
      }

      // Check abort
      if (abortController.signal.aborted) {
        active = false;
        const err = createSimulationError('TIMEOUT', 'Polling was cancelled.');
        onError?.(err);
        reject(err);
        return;
      }

      try {
        const result = await getSimulationResults(simulationId);

        // Re-check cancellation/timeout after async fetch completes
        if (!active || abortController.signal.aborted) {
          active = false;
          const err = createSimulationError('TIMEOUT', 'Polling was cancelled.');
          onError?.(err);
          reject(err);
          return;
        }

        const elapsedAfterFetch = performance.now() - startTime;
        if (elapsedAfterFetch >= timeoutMs) {
          active = false;
          const err = createSimulationError('TIMEOUT', `Polling timed out after ${timeoutMs}ms.`);
          onError?.(err);
          reject(err);
          return;
        }

        // Notify update on every poll
        onUpdate?.(result);

        // Terminal states
        if (result.status === 'success' || result.status === 'error') {
          active = false;
          if (result.status === 'success') {
            onComplete?.(result);
            resolve(result);
          } else {
            const err = createSimulationError('SIMULATION_FAILED', 'Simulation completed with error status.');
            onError?.(err);
            reject(err);
          }
          return;
        }

        // Wait before next poll
        await sleep(intervalMs);
      } catch (err) {
        active = false;
        const simErr = isSimulationError(err) ? err : createSimulationError('UNKNOWN', String(err));
        onError?.(simErr);
        reject(simErr);
        return;
      }
    }
  });

  return {
    promise,
    cancel: () => {
      active = false;
      abortController.abort();
    },
    isActive: () => active,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  REQUEST CANCELLATION (Phase 2.2)
// ═══════════════════════════════════════════════════════════════════════

/** Active AbortController for the current simulation request */
let activeRequestController: AbortController | null = null;

/** Active PollingController for the current poll loop */
let activePollingController: PollingController | null = null;

/**
 * Cancel any in-flight simulation request and active polling.
 * Safe to call multiple times or when nothing is active.
 */
export function cancelActiveSimulation(): void {
  if (activeRequestController) {
    SimulationLogger.info('Cancelling active request');
    activeRequestController.abort();
    activeRequestController = null;
  }
  if (activePollingController) {
    SimulationLogger.info('Cancelling active polling');
    activePollingController.cancel();
    activePollingController = null;
  }
}

/**
 * Create a new AbortController for a request, cancelling any previous one.
 * Returns the signal to pass into fetch.
 */
export function createRequestController(): AbortController {
  // Cancel previous request if still active
  if (activeRequestController) {
    activeRequestController.abort();
  }
  activeRequestController = new AbortController();
  return activeRequestController;
}

/**
 * Track a polling controller as the active one (for page-leave cleanup).
 */
export function setActivePollingController(controller: PollingController): void {
  // Cancel previous polling if still active
  if (activePollingController && activePollingController.isActive()) {
    activePollingController.cancel();
  }
  activePollingController = controller;
}

/**
 * Get the currently active polling controller (for external cancellation).
 */
export function getActivePollingController(): PollingController | null {
  return activePollingController;
}

// ═══════════════════════════════════════════════════════════════════════
//  CONCURRENCY GUARD (Phase 2.3)
// ═══════════════════════════════════════════════════════════════════════

export type ConcurrencyPolicy = 'block' | 'replace';

let concurrencyPolicy: ConcurrencyPolicy = 'replace';
let simulationActive = false;

export function setConcurrencyPolicy(policy: ConcurrencyPolicy): void {
  concurrencyPolicy = policy;
}

export function getConcurrencyPolicy(): ConcurrencyPolicy {
  return concurrencyPolicy;
}

export function isSimulationActive(): boolean {
  return simulationActive;
}

export function setSimulationActive(active: boolean): void {
  simulationActive = active;
}

/**
 * Guard that enforces concurrency policy before starting a new simulation.
 * Returns true if execution should proceed, false if blocked.
 * In 'replace' mode, cancels the active simulation and returns true.
 * In 'block' mode, throws a SimulationError if a sim is already running.
 */
export function enforceConcurrencyGuard(): boolean {
  if (!simulationActive) return true;

  if (concurrencyPolicy === 'replace') {
    SimulationLogger.info('Concurrency guard: replacing active simulation');
    cancelActiveSimulation();
    return true;
  }

  // block mode
  SimulationLogger.info('Concurrency guard: blocking — simulation already active');
  throw createSimulationError(
    'INVALID_REQUEST',
    'A simulation is already running. Wait for it to complete or cancel it.',
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  RUNTIME RESULT VALIDATION (Phase 2.4)
// ═══════════════════════════════════════════════════════════════════════

const RESULT_VALIDATION_RULES: ResultValidationRule[] = [
  {
    field: 'metrics.totalReturn',
    validate: (v) => typeof v === 'number' && !isNaN(v as number),
    message: 'totalReturn must be a valid number',
  },
  {
    field: 'metrics.totalReturnPct',
    validate: (v) => typeof v === 'number' && !isNaN(v as number),
    message: 'totalReturnPct must be a valid number',
  },
  {
    field: 'metrics.winRate',
    validate: (v) => typeof v === 'number' && (v as number) >= 0 && (v as number) <= 100,
    message: 'winRate must be between 0 and 100',
  },
  {
    field: 'metrics.maxDrawdownPct',
    validate: (v) => typeof v === 'number',
    message: 'maxDrawdownPct must be a number',
  },
  {
    field: 'metrics.sharpeRatio',
    validate: (v) => typeof v === 'number' && !isNaN(v as number),
    message: 'sharpeRatio must be a valid number',
  },
  {
    field: 'metrics.totalTrades',
    validate: (v) => typeof v === 'number' && Number.isInteger(v) && (v as number) >= 0,
    message: 'totalTrades must be a non-negative integer',
  },
  {
    field: 'positions',
    validate: (v) => Array.isArray(v),
    message: 'positions must be an array',
  },
  {
    field: 'timeline',
    validate: (v) => Array.isArray(v),
    message: 'timeline must be an array',
  },
  {
    field: 'simulationId',
    validate: (v) => typeof v === 'string' && (v as string).length > 0,
    message: 'simulationId must be a non-empty string',
  },
];

/**
 * Validate a SimulationResult at runtime.
 * Throws SimulationError with code INVALID_RESPONSE if any rule fails.
 */
export function validateSimulationResult(result: SimulationResult): void {
  const errors: string[] = [];

  for (const rule of RESULT_VALIDATION_RULES) {
    const value = getNestedField(result, rule.field);
    if (!rule.validate(value)) {
      errors.push(`${rule.field}: ${rule.message} (got ${JSON.stringify(value)})`);
    }
  }

  // Additional cross-field validations
  if (result.metrics) {
    const m = result.metrics;
    if (m.winningTrades + m.losingTrades > m.totalTrades * 1.01) {
      errors.push('winningTrades + losingTrades exceeds totalTrades');
    }
  }

  if (errors.length > 0) {
    SimulationLogger.error('Result validation failed', { errors });
    throw createSimulationError(
      'INVALID_RESPONSE',
      `Result validation failed: ${errors.join('; ')}`,
      { validationErrors: errors },
    );
  }
}

function getNestedField(obj: Record<string, any>, path: string): unknown {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// ═══════════════════════════════════════════════════════════════════════
//  ENHANCED ERROR CLASSIFICATION (Phase 2.7)
// ═══════════════════════════════════════════════════════════════════════

export function classifyError(error: SimulationError): SimulationErrorType {
  return ERROR_CODE_TO_TYPE[error.code] || 'server';
}

export function isRetryableError(error: SimulationError): boolean {
  const type = classifyError(error);
  return type === 'network' || type === 'timeout' || type === 'rate_limit';
}
